import React, { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '../../utils/cn';

/**
 * Compact filter dropdown.
 *
 * Replaces native <select> in filter rows. A native select is the wrong
 * control here for three concrete reasons, all of which were visible on the
 * dashboard:
 *
 *  1. It CLIPS its selected text instead of ellipsising it, so a long option
 *     ("CFO - Executive Summary") rendered as "CFO - Executive Summar" with
 *     the browser's arrow sitting on top of the cut.
 *  2. Its dropdown arrow is drawn by the browser, so it can never be made to
 *     match a custom control beside it - the dashboard had one custom
 *     provider dropdown and two native selects in the same row, and they
 *     looked like three different widgets.
 *  3. Sizing it to fit the longest option makes the control permanently as
 *     wide as its worst case; sizing it smaller reintroduces (1).
 *
 * This renders a button + popover instead: the trigger auto-sizes to the
 * SELECTED label (so it's only as wide as it needs to be, and truncates with
 * a real ellipsis if constrained), and the menu is free to show a longer
 * description per option that the trigger doesn't have to accommodate.
 */

export interface FilterSelectOption<T extends string = string> {
  value: T;
  /** Short label shown in the trigger and as the option's title. */
  label: string;
  /** Optional longer gloss shown only inside the menu. */
  description?: string;
  /**
   * Per-option glyph, rendered in the menu row and - for the selected
   * option - in the trigger. Takes a ready-made node rather than a component
   * type so callers can supply a brand-coloured SVG (the provider filter
   * needs AWS orange / Azure sky / GCP emerald), which a bare icon type
   * can't express.
   */
  icon?: React.ReactNode;
  /** CSS colour applied to `icon`. */
  iconColor?: string;
}

export interface FilterSelectProps<T extends string = string> {
  /** Accessible name. Not rendered visually - the trigger shows the value. */
  label: string;
  value: T;
  options: Array<FilterSelectOption<T>>;
  onChange: (value: T) => void;
  icon?: React.ElementType;
  className?: string;
  /**
   * Which edge the popover aligns to. Defaults to 'start' (left-aligned)
   * because these controls live in left-aligned filter rows; a right-aligned
   * menu on the leftmost control in the row hangs off the left of the
   * viewport and clips its own check marks and icons.
   */
  align?: 'start' | 'end';
}

export function FilterSelect<T extends string = string>({
  label,
  value,
  options,
  onChange,
  icon: Icon,
  className,
  align = 'start',
}: FilterSelectProps<T>) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const selected = options.find((o) => o.value === value) || options[0];

  // Keyboard support on the trigger: Enter/Space to toggle, arrows to move
  // through options, Escape to dismiss. Carried over from the provider
  // dropdown this component replaced - that one had full arrow-key
  // navigation, and consolidating shouldn't quietly cost accessibility.
  const onTriggerKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    const idx = options.findIndex((o) => o.value === value);
    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        setOpen((v) => !v);
        break;
      case 'Escape':
        setOpen(false);
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (!open) setOpen(true);
        else onChange(options[(idx + 1) % options.length].value);
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (!open) setOpen(true);
        else onChange(options[(idx - 1 + options.length) % options.length].value);
        break;
    }
  };

  // Close on outside click / Escape. Both are needed: a popover that only
  // closes by re-clicking its own trigger is a trap once the menu is wider
  // than the trigger.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div ref={wrapRef} className={cn('relative', className)}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        onKeyDown={onTriggerKeyDown}
        aria-label={`${label}: ${selected?.label ?? ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex h-8 max-w-[240px] shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md border bg-card px-2.5 text-xs font-medium text-foreground shadow-sm transition-colors hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-ring"
      >
        {selected?.icon ? (
          <span
            className="flex h-3.5 w-3.5 shrink-0 items-center justify-center [&_svg]:h-3.5 [&_svg]:w-3.5"
            style={selected.iconColor ? { color: selected.iconColor } : undefined}
            aria-hidden="true"
          >
            {selected.icon}
          </span>
        ) : (
          Icon && <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        )}
        <span className="truncate">{selected?.label}</span>
        <ChevronDown className={cn('h-3 w-3 shrink-0 opacity-50 transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div
          role="listbox"
          aria-label={label}
          // An absolute min-width, NOT min-w-full: the menu is often wider
          // than its trigger (the trigger shows "CFO", the menu also shows
          // "Executive summary"), and inheriting the trigger's width made
          // every description wrap onto two or three lines.
          className={cn(
            'absolute z-50 mt-1 w-max min-w-[13rem] max-w-[18rem] overflow-hidden rounded-md border bg-popover p-1 shadow-md',
            align === 'end' ? 'right-0' : 'left-0'
          )}
        >
          {options.map((opt) => {
            const isSelected = opt.value === value;
            return (
              <button
                key={opt.value}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
                className={cn(
                  'flex w-full items-start gap-2 rounded-sm px-2 py-1.5 text-left text-xs transition-colors',
                  isSelected ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-secondary'
                )}
              >
                <Check className={cn('mt-0.5 h-3 w-3 shrink-0', isSelected ? 'opacity-100' : 'opacity-0')} />
                {opt.icon && (
                  <span
                    className="flex h-3.5 w-3.5 shrink-0 items-center justify-center [&_svg]:h-3.5 [&_svg]:w-3.5"
                    style={opt.iconColor ? { color: opt.iconColor } : undefined}
                    aria-hidden="true"
                  >
                    {opt.icon}
                  </span>
                )}
                <span className="min-w-0">
                  <span className="block whitespace-nowrap font-medium">{opt.label}</span>
                  {opt.description && (
                    <span className="mt-0.5 block whitespace-nowrap text-2xs text-muted-foreground">
                      {opt.description}
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
